import React from 'react';
import styled from 'styled-components';
import { Percent, DollarSign, Tag, Gift } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

const TYPE_OPTIONS = [
    {
        value: 'percentage',
        label: 'Percentual',
        description: 'Ex: 20% de desconto',
        Icon: Percent,
        color: panelAccent(colors.occasionalPurple),
    },
    {
        value: 'fixed',
        label: 'Valor fixo',
        description: 'Ex: R$ 5,00 de desconto',
        Icon: DollarSign,
        color: colors.primary,
    },
    {
        value: 'fixed_price',
        label: 'Preço fixo',
        description: 'Ex: produto por R$ 10,00',
        Icon: Tag,
        color: colors.occasionalGreen,
    },
    {
        value: 'bundle',
        label: 'Combo',
        description: 'Ex: compre 1 leve 2',
        Icon: Gift,
        color: colors.occasionalOrange,
    },
];

export default function StepBasicInfo({ data, onChange }) {
    function set(key, value) {
        onChange({ ...data, [key]: value });
    }

    return (
        <Container>
            <Field>
                <Label>Nome da promoção *</Label>
                <Input
                    value={data.name}
                    onChange={e => set('name', e.target.value)}
                    placeholder='Ex: Happy Hour 20% off'
                    autoFocus
                />
            </Field>

            <Field>
                <Label>Tipo de desconto *</Label>
                <TypeGrid>
                    {TYPE_OPTIONS.map(opt => {
                        const Icon = opt.Icon;
                        const selected = data.type === opt.value;
                        return (
                            <TypeCard
                                key={opt.value}
                                type='button'
                                $selected={selected}
                                $color={opt.color}
                                onClick={() => set('type', opt.value)}
                            >
                                <TypeIconWrap $selected={selected} $color={opt.color}>
                                    <Icon size={18} />
                                </TypeIconWrap>
                                <TypeLabel $selected={selected}>{opt.label}</TypeLabel>
                                <TypeDesc>{opt.description}</TypeDesc>
                            </TypeCard>
                        );
                    })}
                </TypeGrid>
            </Field>

            <TwoCol>
                <Field>
                    <Label>Limite de usos</Label>
                    <Input
                        type='number'
                        min='1'
                        value={data.max_uses}
                        onChange={e => set('max_uses', e.target.value)}
                        placeholder='Ilimitado'
                    />
                    <Hint>Deixe em branco para ilimitado</Hint>
                </Field>

                <Field>
                    <Label>Status</Label>
                    <ToggleRow>
                        <ToggleInfo>
                            <ToggleName>Ativa</ToggleName>
                            <ToggleHint>Promoção em vigor</ToggleHint>
                        </ToggleInfo>
                        <ToggleSwitch
                            type='button'
                            $on={data.is_active}
                            onClick={() => set('is_active', !data.is_active)}
                            role='switch'
                            aria-checked={data.is_active}
                        >
                            <ToggleKnob $on={data.is_active} />
                        </ToggleSwitch>
                    </ToggleRow>
                </Field>
            </TwoCol>
        </Container>
    );
}

const Container = styled.div`display: flex; flex-direction: column; gap: 20px;`;

const Field = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const Label = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Hint = styled.span`font-size: 11px; color: ${colors.nightLight};`;

const Input = styled.input`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 15px;
    outline: none;
    color: ${colors.nightDark};
    background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
    &::placeholder { color: #999; }
`;

const TypeGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const TypeCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 14px;
    border-radius: 12px;
    border: 1.5px solid ${({ $selected, $color }) => $selected ? $color : colors.neutralDark};
    background: ${({ $selected, $color }) => $selected ? `${$color}0D` : '#fff'};
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
    &:hover { border-color: ${({ $color }) => $color}; }
`;

const TypeIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $selected, $color }) => $selected ? `${$color}20` : colors.neutralDark};
    color: ${({ $selected, $color }) => $selected ? $color : colors.nightLight};
    transition: all 0.15s;
`;

const TypeLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ $selected }) => $selected ? colors.nightDark : colors.night};
`;

const TypeDesc = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    line-height: 1.3;
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    gap: 10px;
    background: #fff;
`;

const ToggleInfo = styled.div`display: flex; flex-direction: column; gap: 1px;`;
const ToggleName = styled.span`font-size: 14px; font-weight: 600; color: ${colors.nightDark};`;
const ToggleHint = styled.span`font-size: 11px; color: ${colors.nightLight};`;

const ToggleSwitch = styled.button`
    width: 42px;
    height: 24px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    background: ${({ $on }) => $on ? colors.occasionalGreen : colors.neutralDark};
    position: relative;
    flex-shrink: 0;
    transition: background 0.2s;
    padding: 0;
`;

const ToggleKnob = styled.div`
    position: absolute;
    top: 3px;
    left: ${({ $on }) => $on ? '21px' : '3px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: left 0.2s;
`;
